<template>
  <div ref="searchBar" class="w-full">
    <div class="flex gap-16">
      <ClientOnly fallbackTag="div">
        <Textbox
          ref="textbox"
          v-model="query"
          :input-id="'search'"
          :is-listbox-open="isDropdownOpen"
          :listbox-results-message="
            query
              ? i18n(translations.resultsListing, {
                  resultsCount: visibleProductsHits.length,
                  searchQuery: query,
                })
              : i18n(translations.textboxPlaceholder)
          "
          @submit="handleTextBoxSubmit"
        />

        <button
          v-show="isSearchBarActive"
          class="text-nowrap md:hidden"
          type="button"
          @click="closeSearchBar"
        >
          {{ i18n(translations.cancelLabel) }}
        </button>
        <template #fallback>
          <div
            class="bg-action-default-mid text-action-default-hi rounded-full h-[40px] w-full gap-4 pl-4 pr-8"
          ></div>
        </template>
      </ClientOnly>
    </div>

    <Dropdown :is-open="isDropdownOpen" :is-touch-device>
      <div v-show="isSearchResultsVisible">
        <div
          v-show="!isFetchingProductAndSuggestions && !isTyping"
          class="flex flex-col-reverse gap-16 pt-24 md:flex-row"
        >
          <div class="px-24 md:min-w-256 md:pb-40 md:pl-40 md:pr-0">
            <ShoppingAssistantButton
              v-if="isShoppingAssistantWebEnabled"
              @click="handleSearchWithAi"
            />
            <SuggestionsSection
              :input-value="query"
              name="suggestions"
              :suggestions="suggestionsHits"
              :title="i18n(translations.suggestedSearches)"
              @select="handleSuggestionSectionSelect"
              @submit="handleSuggestionSectionSubmit"
            />
          </div>
          <ProductSection
            class="grow px-24 md:px-0"
            :products="productsHits"
            @click-product="closeSearchBar"
          />
        </div>
        <Skeleton v-show="!(!isFetchingProductAndSuggestions && !isTyping)" />
      </div>

      <div v-show="!isSearchResultsVisible">
        <div
          v-if="
            isBrowsingHistoryEnabled &&
            (isRecentlyViewedPagesPending || recentlyViewedPages.length > 0)
          "
          class="flex flex-col-reverse md:grid md:grid-cols-[256px_auto] gap-16 pt-24 md:flex-row"
        >
          <div class="px-24 md:pb-40 md:pl-40 md:pr-0">
            <ShoppingAssistantButton
              v-if="isShoppingAssistantWebEnabled"
              class="hidden md:block"
              @click="handleSearchWithAi"
            />
            <SuggestionsSection
              name="popularSearches"
              :suggestions="popularSearches"
              :title="i18n(translations.popularSearches)"
              @select="handleSuggestionSectionSelect"
              @submit="handleSuggestionSectionSubmit"
            />
          </div>
          <div class="px-24 md:px-0">
            <ShoppingAssistantButton
              v-if="isShoppingAssistantWebEnabled"
              class="md:hidden"
              @click="handleSearchWithAi"
            />
            <RecentlyViewedSection
              :recently-viewed-pages
              :pending="isRecentlyViewedPagesPending"
            />
          </div>
        </div>
        <div v-else class="mx-auto max-w-[650px] px-28 pb-40 pt-24">
          <ShoppingAssistantButton
            v-if="isShoppingAssistantWebEnabled"
            @click="handleSearchWithAi"
          />
          <SuggestionsSection
            name="popularSearches"
            :suggestions="popularSearches"
            :title="i18n(translations.popularSearches)"
            @select="handleSuggestionSectionSelect"
            @submit="handleSuggestionSectionSubmit"
          />
        </div>
      </div>
    </Dropdown>

    <ClientOnly>
      <ShoppingAssistantNewConversationDialog
        v-if="isShoppingAssistantWebEnabled"
        :name="SEARCH_AI_CONFIRM_DIALOG_NAME"
        @accept="handleConfirmNewConversation"
        @decline="handleDeclineNewConversation"
      />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRouteLocationWithLocale, useState } from '#imports'
import { computed, defineAsyncComponent, ref, useTemplateRef, watch } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import {
  useEventListener,
  useFocusWithin,
  useScrollLock,
  useTimeoutFn,
} from '@vueuse/core'

import { useBrowsingHistory } from '~/scopes/browsing-history/composables/useBrowsingHistory'
import { ROUTES as PRODUCT_LIST_ROUTES } from '~/scopes/product-list/route-names'
import { useAssistedSearchExperiment } from '~/scopes/shopping-assistant/composables/useAssistedSearchExperiment'
import { useIsShoppingAssistantWebEnabled } from '~/scopes/shopping-assistant/composables/useIsShoppingAssistantWebEnabled'
import { useShoppingAssistantEntry } from '~/scopes/shopping-assistant/composables/useShoppingAssistantEntry'
import { useShoppingAssistantWebSidebar } from '~/scopes/shopping-assistant/stores/useShoppingAssistantWebSidebar'

import { useSearchQueryParam } from '../../composables/useSearchQueryParam/useSearchQueryParam'

import translations from './SearchBar.translations'
import Dropdown from './components/Dropdown/Dropdown.vue'
import ProductSection from './components/ProductSection/ProductSection.vue'
import RecentlyViewedSection from './components/RecentlyViewedSection/RecentlyViewedSection.vue'
import ShoppingAssistantButton from './components/ShoppingAssistantButton/ShoppingAssistantButton.vue'
import Skeleton from './components/Skeleton/Skeleton.vue'
import type { Suggestion } from './components/SuggestionsSection/SuggestionsSection.types'
import SuggestionsSection from './components/SuggestionsSection/SuggestionsSection.vue'
import Textbox from './components/TextBox/Textbox.vue'
import { useAlgoliaSearch } from './composables/useAlgoliaSearch/useAlgoliaSearch'
import { useFetchPopularSearches } from './composables/useFetchPopularSearches/useFetchPopularSearches'
import { useFetchSearchBarConfiguration } from './composables/useFetchSearchBarConfiguration/useFetchSearchBarConfiguration'
import { useIsTyping } from './composables/useIsTyping/useIsTyping'
import { useRecentlyViewedPages } from './composables/useRecentlyViewedPages/useRecentlyViewedPages'
import { useToggleSearchBar } from './composables/useToggleSearchBar'
import { useVisibleProducts } from './composables/useVisibleProducts/useVisibleProducts'

const ShoppingAssistantNewConversationDialog = defineAsyncComponent(
  () =>
    import('~/scopes/shopping-assistant/components/ShoppingAssistantNewConversationDialog.vue'),
)

const i18n = useI18n()
const tracking = useTracking()
const experiments = useExperiments()
const resolveLocalizedRoute = useRouteLocationWithLocale()

const textboxRef = useTemplateRef('textbox')
const searchBarRef = useTemplateRef('searchBar')

const query = ref<string>('')
const isTouchDevice = ref(false)

const { openSearchBar, closeSearchBar, isSearchBarActive } =
  useToggleSearchBar()

const isBrowsingHistoryEnabled = computed(
  () => experiments['experiment.browsingHistoryPanel'] === 'enabled',
)

const browsingHistory = useBrowsingHistory()

const hasTrackedExposure = useState(
  'search-bar-history-exposure-tracked',
  () => false,
)

async function trackPanelOpen() {
  const entries = await browsingHistory.fetch()

  tracking.trackSearchPanelOpen({
    has_history: entries.length > 0,
  })
}

function trackExposureOnce() {
  if (hasTrackedExposure.value) return

  hasTrackedExposure.value = true
  tracking.trackExperimentExposure({
    experiment: 'browsing_history_panel',
    experimentArm: experiments['experiment.browsingHistoryPanel'],
  })
}
const isShoppingAssistantWebEnabled = useIsShoppingAssistantWebEnabled()
const shoppingAssistant = useShoppingAssistantWebSidebar()
const { shouldAssistSearch, isEligible, isAssigned } =
  useAssistedSearchExperiment()

const SEARCH_AI_CONFIRM_DIALOG_NAME = 'search-bar-ai-new-conversation'

const {
  launch: launchAssistant,
  accept: handleConfirmNewConversation,
  decline: handleDeclineNewConversation,
} = useShoppingAssistantEntry({
  dialogName: SEARCH_AI_CONFIRM_DIALOG_NAME,
  entryPoint: 'search',
  onLaunch: ({ message }) => trackAiOpen(message),
})

function trackAiOpen(message: string) {
  tracking.trackClick({
    zone: 'header_searchbar',
    name: 'ai_shopping_assistant_opened',
    entryPoint: 'search',
    seeded: message.length > 0,
  })
}

function handleSearchWithAi() {
  const message = query.value.trim()
  query.value = ''
  closeSearchBar()

  launchAssistant({ message })
}

const { configuration } = useFetchSearchBarConfiguration()
const { isTyping } = useIsTyping(query)
const {
  productsHits,
  suggestionsHits,
  isFetchingProductAndSuggestions,
  searchProductsAndSuggestions,
} = useAlgoliaSearch({ query, configuration })

const { data: popularSearches } = useFetchPopularSearches()

const {
  data: recentlyViewedPages,
  execute: fetchRecentlyViewedPages,
  pending: isRecentlyViewedPagesPending,
} = useRecentlyViewedPages(configuration)

const { visibleProducts: visibleProductsHits } = useVisibleProducts(
  () => productsHits.value,
)

const isSearchResultsVisible = computed(
  () =>
    query.value.length > 0 &&
    (productsHits.value.length > 0 ||
      suggestionsHits.value.length > 0 ||
      isFetchingProductAndSuggestions.value ||
      isTyping.value),
)

const isDropdownOpen = computed(
  () =>
    isSearchBarActive.value &&
    (isSearchResultsVisible.value || popularSearches.value?.length > 0),
)

watch(isDropdownOpen, (newIsDropdownOpen) => {
  if (newIsDropdownOpen) {
    fetchRecentlyViewedPages()
  }
})

const searchQueryParam = useSearchQueryParam()

const historySelection = ref<{ title: string } | null>(null)

watch(
  searchQueryParam,
  (newSearchQuery) => {
    if (newSearchQuery) {
      query.value = newSearchQuery
    }
  },
  { immediate: true },
)

watch(query, () => {
  if (query.value === '') {
    productsHits.value = []
    suggestionsHits.value = []
  }
})

watch(isTyping, () => {
  if (!isTyping.value) {
    searchProductsAndSuggestions()
  }
})

async function handleTextBoxSubmit() {
  const message = query.value.trim()
  const shouldFireAssistedSearch = shouldAssistSearch()

  const searchRoute = resolveLocalizedRoute({
    name: PRODUCT_LIST_ROUTES.SEARCH_RESULTS,
    query: {
      q: query.value,
    },
  })
  await navigateTo(searchRoute as RouteLocationRaw)

  if (shouldFireAssistedSearch) {
    shoppingAssistant.restart({ entryPoint: 'assisted_search', message })
  }

  tracking.trackFormSubmit({
    zone: 'header_searchbar',
    name: query.value,
    payload: {
      index_used: 0,
      search_type: 'search_page_results',
      ...(isEligible.value && { isAssistedSearch: isAssigned.value }),
      ...(historySelection.value?.title === query.value && {
        source: 'history',
      }),
    },
  })
  historySelection.value = null
  closeSearchBar()
}

function handleSuggestionSectionSelect(
  title: string,
  entryType?: Suggestion['entry_type'],
) {
  query.value = title
  historySelection.value = entryType !== undefined ? { title } : null
  textboxRef.value?.focusInput()
}

function handleSuggestionSectionSubmit() {
  closeSearchBar()
}

if (import.meta.client) {
  useEventListener(
    searchBarRef,
    'touchstart',
    () => {
      isTouchDevice.value = true
    },
    { once: true },
  )

  const { focused: isFocused } = useFocusWithin(searchBarRef)

  const { start: scheduleCloseSearchBar, stop: stopCloseSearchBar } =
    useTimeoutFn(closeSearchBar, 150)

  watch(isFocused, () => {
    if (isFocused.value) {
      stopCloseSearchBar()
      openSearchBar()
    } else {
      scheduleCloseSearchBar()
    }
  })

  const isPageScrollLocked = useScrollLock(document?.body)

  watch(isDropdownOpen, (isOpen) => {
    isPageScrollLocked.value = isOpen

    if (isOpen) {
      trackPanelOpen()
      trackExposureOnce()
    }
  })
}
</script>
